import { useDebounce } from '@supp/hooks';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Typography,
  notification,
  Progress,
  Steps,
} from 'antd';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { uploadArtwork } from '../../api/supp';
import { IMAGE_BASE_URL } from '../../consts/image';
import { Job } from '../../types/jobs';
import LoginPage from '../img/login';
import type { UploadArtworkParams } from '../../api/supp';

const { Title } = Typography;

const REFRESH_TOKEN_KEY = 'nightcafe_refresh_token';

const ImageScraperUploader: React.FC = () => {
  const [images, setImages] = useState<Job[]>([]);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [authToken, setAuthToken] = useState<string>();
  const [suppToken, setSuppToken] = useState<string>('');
  const [state, setState] = useState({
    needLoadCount: 0,
    needSelectCount: 0,
    lastVisibleId: null as string | null,
    loading: false,
    isLoggedIn: false,
  });
  const observer = useRef<IntersectionObserver>();
  const [uploadProgress, setUploadProgress] = useState(0);

  // 获取 tokens
  const tokensString = localStorage.getItem('tokens');
  const tokens = tokensString ? JSON.parse(tokensString) : [suppToken];

  const uploadArtworkMutation = useMutation({
    mutationFn: (data: UploadArtworkParams) => uploadArtwork(data, data.token),
    onSuccess: (data) => {
      console.log('上传图片成功', data);
    },
    onError: (error) => {
      console.error('上传图片失败', error);
    },
  });

  useEffect(() => {
    if (authToken) {
      fetchImages(true);
    }
  }, [authToken]);

  useEffect(() => {
    refreshAuthToken();
  }, []);

  useEffect(() => {
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (storedRefreshToken) {
      setState((prev) => ({ ...prev, isLoggedIn: true }));
      refreshAuthToken();
    }
  }, []);

  const loadImages = useCallback((count: number) => {
    setState((prev) => ({ ...prev, needLoadCount: count }));
  }, []);

  const setSelectCount = useCallback((count: number) => {
    setState((prev) => ({ ...prev, needSelectCount: count }));
  }, []);

  const loadAndUploadImages = useCallback(
    (count: number) => {
      loadImages(count);
      setSelectCount(count);

      const firstImageID = Array.from(selectedImages)[0];
    },
    [loadImages, setSelectCount]
  );

  const debouncedLoadImages = useDebounce(loadImages, 1000);

  const debouncedSetSelectCount = useDebounce(setSelectCount, 1000);

  const debouncedLoadAndUploadImages = useDebounce(loadAndUploadImages, 1000);

  useEffect(() => {
    if (state.needLoadCount > 0 && images.length < state.needLoadCount) {
      fetchImages(false, state.lastVisibleId);
    }
  }, [state.needLoadCount, images, state.lastVisibleId]);

  useEffect(() => {
    // Select the first needSelectCount images
    if (state.needSelectCount > 0 && images.length >= state.needSelectCount) {
      setSelectedImages(
        new Set(images.slice(0, state.needSelectCount).map((image) => image.id))
      );
    }
  }, [state.needSelectCount, images]);

  const fetchImages = useCallback(
    async (reset = false, currentLastVisibleId: string | null = null) => {
      if (state.loading) return;

      try {
        setState((prev) => ({ ...prev, loading: true }));

        const response = await axios.get(`/api/proxy/v2/creations`, {
          params: {
            query: 'mixed',
            filter: 'none',
            limit: 10,
            lastVisibleId: currentLastVisibleId,
          },
          headers: {
            'x-auth-token': authToken,
          },
        });

        const { results: newImages = [], lastVisibleId: newLastVisibleId } =
          response.data;

        setState((prev) => ({ ...prev, lastVisibleId: newLastVisibleId }));
        setImages((prev) => (reset ? newImages : [...prev, ...newImages]));
      } catch (error) {
        notification.error({
          message: 'Failed to fetch images',
          description: error.message,
        });
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [authToken, state.loading]
  );

  const handleSelectImage = useCallback((id: string) => {
    setSelectedImages((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  }, []);

  const convertImageToFile = async (
    imageUrl: string,
    fileName: string,
    fileType: string
  ) => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new File([blob], `${fileName}.${fileType}`, {
      type: `image/${fileType}`,
    });
  };

  const handleBatchUpload = useCallback(async () => {
    if (selectedImages.size === 0) {
      notification.warning({ message: 'No images selected for upload' });
      return;
    }

    try {
      const totalImages = selectedImages.size;
      let completedUploads = 0;
      let currentTokenIndex = 0;
      const uploadPromises = Array.from(selectedImages).map(async (imageId) => {
        if (tokens) {
          if (currentTokenIndex >= tokens.length) currentTokenIndex = 0;
          const token = tokens[currentTokenIndex++];
          const image = images.find((img) => img.id === imageId);
          if (!image?.output) {
            throw new Error(`Image with ID ${imageId} has no output URL.`);
          }

          let output = image.output.endsWith('.mp4')
            ? image.preUpscaleOutput
            : image.output;

          if (!output) {
            output = image.progressImages?.[0]?.output;
          }
          const fileType = output.split('.').pop();
          const fileName = image.id || `image-${Date.now()}`;

          const fileObj = await convertImageToFile(
            `${IMAGE_BASE_URL}${output}`,
            fileName,
            fileType
          );

          await uploadArtworkMutation.mutateAsync({
            files: [fileObj],
            title: image.title || '',
            description: image?.prompts?.[0] || '',
            tags: ['#AI'],
            token,
          });

          completedUploads++;
          setUploadProgress((completedUploads / totalImages) * 100);
        }
      });

      await Promise.all(uploadPromises);
      notification.success({
        message: 'Batch upload completed',
        description: `Successfully uploaded ${selectedImages.size} images.`,
      });
    } catch (error) {
      notification.error({
        message: 'Upload failed',
        description: error.message,
      });
    }
  }, [selectedImages, images, uploadArtworkMutation]);

  const handleRefreshToken = useCallback((token: string) => {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }, []);

  const refreshAuthToken = useCallback(async () => {
    try {
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!storedRefreshToken) {
        throw new Error('No refresh token found');
      }

      const response = await axios.post(
        'https://securetoken.googleapis.com/v1/token',
        new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: storedRefreshToken,
        }),
        {
          params: {
            key: 'AIzaSyD_bN4JwaaUIuYIOZ2cTvHrh0LRUYTXnfI',
          },
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      setAuthToken(response.data.access_token);
      handleRefreshToken(response.data.refresh_token);
      notification.success({ message: 'Token refreshed successfully' });
    } catch (error) {
      console.error('Failed to refresh token:', error);
      notification.error({ message: 'Failed to refresh token' });
    }
  }, []);

  const handleRefresh = useCallback(() => {
    setState((prev) => ({ ...prev, lastVisibleId: null }));
    fetchImages(true);
  }, [fetchImages]);

  const handleLogout = useCallback(() => {
    setAuthToken(undefined);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setState((prev) => ({ ...prev, isLoggedIn: false }));

    notification.success({ message: 'Logged out successfully' });
  }, []);

  const handleNightCafeLogin = useCallback(
    async (email: string, password: string) => {
      try {
        const response = await axios.post(
          'https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword',
          {
            returnSecureToken: true,
            email,
            password,
            clientType: 'CLIENT_TYPE_WEB',
          },
          {
            params: {
              key: 'AIzaSyD_bN4JwaaUIuYIOZ2cTvHrh0LRUYTXnfI',
            },
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        setAuthToken(response.data.idToken);
        handleRefreshToken(response.data.refreshToken);
        setState((prev) => ({ ...prev, isLoggedIn: true }));
        notification.success({ message: 'Login successful' });
      } catch (error) {
        console.error('Login failed:', error);
        notification.error({ message: 'Login failed' });
      }
    },
    []
  );

  const renderNightCafeCard = () => (
    <Col span={12}>
      <Card className="login-card">
        <Title level={4}>
          NightCafe {state.isLoggedIn ? 'Status' : 'Login'}
        </Title>
        {state.isLoggedIn ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space.Compact style={{ width: '100%', marginTop: '16px' }}>
              <Input.Password
                placeholder="Auth Token"
                value={authToken}
                onChange={(e) => setAuthToken(e.target.value)}
                style={{ flex: 1 }}
              />
              <Button type="default" onClick={refreshAuthToken}>
                Refresh Token
              </Button>
            </Space.Compact>
            <Button type="primary" danger onClick={handleLogout}>
              Logout
            </Button>
          </Space>
        ) : (
          <Form
            onFinish={({ email, password }) =>
              handleNightCafeLogin(email, password)
            }
          >
            <Form.Item
              name="email"
              rules={[{ required: true, message: 'Please input your email!' }]}
              initialValue="guo.li@supp.co.jp"
            >
              <Input placeholder="Email" />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please input your password!' },
              ]}
              // 默认密码 12345678
              initialValue={'12345678'}
            >
              <Input.Password placeholder="Password" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Login
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </Col>
  );

  const lastImageRef = useCallback(
    (node: HTMLDivElement) => {
      if (state.loading) return;

      observer.current?.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          fetchImages(false, state.lastVisibleId);
        }
      });

      if (node) observer.current.observe(node);
    },
    [state.loading, state.lastVisibleId, fetchImages]
  );

  const renderMainContent = () => (
    <>
      <Card className="control-card">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Row justify="center" gutter={[16, 16]}>
            <Col>
              <Button type="primary" onClick={handleRefresh}>
                Refresh Images
              </Button>
            </Col>
            <Col>
              <Button onClick={() => loadImages(100)}>Load 100 Images</Button>
            </Col>
            <Col>
              <Button onClick={() => loadImages(1000)}>Load 1000 Images</Button>
            </Col>
          </Row>

          <Row gutter={[24, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="加载图片数量" style={{ marginBottom: 0 }}>
                <InputNumber
                  value={state.needLoadCount}
                  onChange={(value) => debouncedLoadImages(value)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            {/* <Col xs={24} sm={12} md={8}>
              <Form.Item label="随机选中数量" style={{ marginBottom: 0 }}>
                <InputNumber
                  value={selectedImages.size}
                  onChange={(value) => debouncedSetSelectCount(value)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col> */}
          </Row>

          <div className="status-bar">
            <Space>
              <Typography.Text strong>
                已选择: {selectedImages.size} 张
              </Typography.Text>
              <Typography.Text strong>
                已加载: {images.length} 张
              </Typography.Text>
            </Space>
            <Button
              type="primary"
              onClick={handleBatchUpload}
              disabled={selectedImages.size === 0}
            >
              上传已选图片 ({selectedImages.size})
            </Button>
          </div>
        </Space>
      </Card>

      <Row gutter={[16, 16]} justify="center">
        {images.map((image, index) => {
          console.log(image);
          return (
            <Col
              xs={24}
              sm={12}
              md={8}
              lg={6}
              key={image.id}
              ref={index === images.length - 1 ? lastImageRef : undefined}
            >
              <Card
                hoverable
                style={{ height: '100%' }}
                cover={
                  <div
                    style={{
                      height: '200px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {/* 判断是不是 mp4 */}
                    {image.output.endsWith('.mp4') ? (
                      <video
                        height={200}
                        width="100%"
                        autoPlay
                        controls
                        muted
                        loop
                        playsInline
                        src={`${IMAGE_BASE_URL}${image.output}`}
                      />
                    ) : (
                      // <img
                      //   alt={image.title}
                      //   src={`${IMAGE_BASE_URL}${image?.progressImages?.[0].output}`}
                      //   style={{ width: '100%', objectFit: 'cover' }}
                      // />
                      <img
                        alt={image.title}
                        src={`${IMAGE_BASE_URL}${image.output}`}
                        style={{ width: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </div>
                }
                onClick={() => handleSelectImage(image.id)}
                actions={[
                  <Checkbox
                    checked={selectedImages.has(image.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleSelectImage(image.id);
                    }}
                  >
                    选择
                  </Checkbox>,
                ]}
              >
                <Card.Meta
                  title={image.title}
                  description={
                    <Space>
                      {image.output.endsWith('.mp4') && <span>mp4</span>}
                      <span>浏览: {image.viewsMeta?.numViews}</span>
                      <span>点赞: {image.likesMeta?.numLikes}</span>
                      <span>ID: {image.id}</span>
                    </Space>
                  }
                />
              </Card>
            </Col>
          );
        })}
      </Row>

      {state.loading && (
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Button loading>加载中...</Button>
        </div>
      )}

      <Progress percent={uploadProgress} status="active" />
    </>
  );

  const renderContent = () => {
    let currentStep = 0;
    if (suppToken || tokens) currentStep = 1;
    if ((suppToken || tokens) && state.isLoggedIn) currentStep = 2;

    return (
      <>
        <Card style={{ marginBottom: 24 }}>
          <Steps
            current={currentStep}
            items={[
              {
                title: 'Supp Login',
                description: 'Login to Supp',
              },
              {
                title: 'NightCafe Login',
                description: 'Connect NightCafe',
              },
              {
                title: 'Image Management',
                description: 'Select and Upload',
              },
            ]}
          />
        </Card>

        {!suppToken && !tokens ? (
          <Row justify="center">
            <Col xs={24} sm={20} md={16} lg={12} xl={8}>
              <LoginPage isComponent={true} setCurrentToken={setSuppToken} />
            </Col>
          </Row>
        ) : !state.isLoggedIn ? (
          <Row justify="center">
            <Col xs={24} sm={20} md={16} lg={12}>
              {renderNightCafeCard()}
            </Col>
          </Row>
        ) : (
          <>
            <Row gutter={[24, 24]} justify="space-between">
              <Col xs={24} md={12}>
                <LoginPage isComponent={true} setCurrentToken={setSuppToken} />
              </Col>
              {renderNightCafeCard()}
            </Row>
            {renderMainContent()}
          </>
        )}
      </>
    );
  };

  const styles = `
    .login-card {
      height: 100%;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }

    .control-card {
      margin-bottom: 24px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }

    .status-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f5f5f5;
      padding: 12px 16px;
      border-radius: 8px;
      flex-wrap: wrap;
      gap: 12px;
    }

    @media (max-width: 576px) {
      .status-bar {
        flex-direction: column;
        align-items: stretch;
      }
    }
  `;

  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <Space direction="vertical" style={{ width: '100%', padding: '24px 32px' }}>
      {renderContent()}
    </Space>
  );
};

export default ImageScraperUploader;
